import { Dialog, Transition } from "@headlessui/react";
import CustomButton from "components/common/Button/CustomButton";
import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { IoClose } from "react-icons/io5";

import MultiFileInput from "components/common/FileUpload/MultiFileInput";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_PARTICIAPNT_UPDATE_MEDICATION,
  URL_PARTICIPANT_GET_SINGLE_MEDICATION,
  URL_PARTICIPANT_GET_SINGLE_MEDICATION_HRM,
  URL_PARTICIPANT_REMOVE_MEDICATION_ATTACHMENT,
} from "store/services/URL";
import { getFileExtension } from "Utils/utils";
import { uploadFilesOnVapour } from "Utils/uploadFileOnS3";
import { toast } from "react-toastify";
import SelectOption from "components/common/Select/Select";
import { STATUS_FOR_MEDICATION, USER_ROLE } from "Constants/constant";
import { roleAccess } from "helpers/RolesPermission";

const EditModal = ({
  openEditModal,
  setEditModal,
  singleData,
  participantInfo,
  setUpdatedData,
}) => {
  const { handleSubmit, watch, setValue, register } = useForm();
  const watchFormData = watch();

  const [fileData, setFilesData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [files, setFiles] = useState([]);
  const [getResponseLoader, setResponseLoader] = useState(false);
  const [selectedMedication, setMedication] = useState(null);

  const [isFileDeleteLoader, setIsFileDeleteLoader] = useState(null);
  const [administeredByLabel, setAdministeredByLabel] = useState("");
  const [administeredTimeLabel, setAdministeredTimeLabel] = useState("");
  const memoizedSetMedication = useCallback((medication) => {
    setMedication(medication);
  }, []);

  const memoizedSetFiles = useCallback((e) => {
    setFiles(e);
  }, []);

  useEffect(() => {
    if (participantInfo?.uuid && singleData?.uuid) {
      setResponseLoader(true);
      setAdministeredByLabel("");
      setAdministeredTimeLabel("");
      setFilesData([]);
      const URL = roleAccess([USER_ROLE?.Sp_Standard_User])
        ? URL_PARTICIPANT_GET_SINGLE_MEDICATION_HRM
        : URL_PARTICIPANT_GET_SINGLE_MEDICATION;
      apiGetRequest(
        `${URL}?pt_id=${participantInfo?.uuid}&medication_log_id=${singleData?.uuid}`
      )
        .then((res) => {
          const response = res?.data?.data;

          setValue("notes", response?.notes || "");
          setValue("comments", response?.comment || "");
          const medicationReason = STATUS_FOR_MEDICATION?.filter(
            (item) => item?.value === `${response?.status_letter}`
          );
          setMedication(medicationReason[0] || null);
          const byWorker = (response?.administrated_by || "").trim();
          setAdministeredByLabel(byWorker || "N/A");
          const atTime = (response?.administered_time || "").trim();
          setAdministeredTimeLabel(atTime || "N/A");
          let parsed = [];
          try {
            const raw = response?.attachments;
            if (Array.isArray(raw)) {
              parsed = raw;
            } else if (typeof raw === "string" && raw.trim() !== "") {
              parsed = JSON.parse(raw);
            } else {
              parsed = [];
            }
          } catch (e) {
            parsed = [];
          }
          setFiles(Array.isArray(parsed) ? parsed : []);
        })
        .catch((error) => {
          // Error handled by loader state
        })
        .finally(() => {
          setResponseLoader(false);
        });
    }
  }, [participantInfo?.uuid, singleData?.uuid, setValue]);

  const onSubmit = async (data) => {
    if (
      selectedMedication === null ||
      selectedMedication === undefined ||
      selectedMedication === ""
    ) {
      toast.error("Please select status for medication.");
      return;
    }

    setResponseLoader(true);

    const payload = {
      pt_id: singleData?.pt_id,
      medication_log_id: singleData?.id,
      status: selectedMedication?.label,
      status_letter: selectedMedication?.value,
      notes: data?.notes,
      comment: data?.comments,
    };

    const fileToUpload = (files || []).filter((fl) => fl instanceof File);
    if (fileToUpload.length > 0) {
      try {
        // Upload on save so attachment keys are always included in the update payload.
        const uploadResults = await uploadFilesOnVapour(fileToUpload);
        const preparFilesData = (uploadResults || [])
          .filter((item) => item?.key)
          .map((item) => ({
            key: item.key,
            ext: item.ext || getFileExtension(item.name) || "bin",
            name: item.name,
          }));

        if (preparFilesData.length !== fileToUpload.length) {
          toast.error(
            "Failed to upload one or more attachments. Please try again."
          );
          setResponseLoader(false);
          return;
        }
        payload.attachments = preparFilesData;
      } catch (error) {
        toast.error("Failed to upload attachments. Please try again.");
        setResponseLoader(false);
        return;
      }
    }

    apiPostRequest(URL_PARTICIAPNT_UPDATE_MEDICATION, payload)
      .then((res) => {
        const saved = res?.data?.data;
        setUpdatedData(saved);
        const byWorker = (saved?.administrated_by || "").trim();
        setAdministeredByLabel(byWorker || "N/A");
        const atTime = (saved?.administered_time || "").trim();
        setAdministeredTimeLabel(atTime || "N/A");
        toast.success("Medication updated successfully...!");
        setEditModal(false);
      })
      .catch((error) => {
        toast.error(error.message || "Something went wrong!");
      })
      .finally(() => {
        setResponseLoader(false);
      });
  };

  const handleDeleteItemCall = (item, callback) => {
    try {
      const payload = {
        pt_id: singleData?.pt_id,
        medication_log_id: singleData?.id,
        attachments: item,
      };
      setIsFileDeleteLoader(item?.original_name);
      apiPostRequest(URL_PARTICIPANT_REMOVE_MEDICATION_ATTACHMENT, payload)
        .then((res) => {
          let nextFiles = [];
          try {
            const raw = res?.data?.data?.attachments;
            if (Array.isArray(raw)) {
              nextFiles = raw;
            } else if (typeof raw === "string" && raw.trim() !== "") {
              nextFiles = JSON.parse(raw);
            }
          } catch (e) {
            nextFiles = [];
          }
          setFiles(Array.isArray(nextFiles) ? nextFiles : []);
          setUpdatedData(res?.data?.data);
          setIsFileDeleteLoader(null);

          if (callback) callback();
        })
        .catch((error) => {
          setIsFileDeleteLoader(null);
          toast.error(error?.message || "Something Went Wrong!");
        });
    } catch (err) {
      setIsFileDeleteLoader(null);
      toast.error(err?.message || "Something Went Wrong!");
    }
  };
  return (
    <Transition.Root as={Fragment} show={openEditModal}>
      <Dialog as='div' className='relative z-[99]' onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full items-end justify-center text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel
                style={{
                  minWidth: "500px",
                  maxWidth: "500px",
                  height: "auto",
                  overflowY: "auto",
                }}
                className={`relative transform overflow-hidden rounded-xl bg-white text-left transition-all`}
              >
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className='p-2 bg-white'>
                   
                    <div className='w-full flex justify-end'>
                      <IoClose
                        className='w-6 h-6 text-[#FF456E] cursor-pointer'
                        onClick={() => setEditModal(false)}
                      />
                    </div>
                    <div className='p-2 '>
                      <div className="max-h-[400px] overflow-y-auto custom-scroll px-2">
                      <label className='text-base font-semibold mt-2 block'>
                        Status:<span className="text-sm text-red-500">*</span>
                      </label>
                      <div className=''>
                        <SelectOption
                          data={STATUS_FOR_MEDICATION || []}
                          selectedOption={selectedMedication}
                          handleChange={memoizedSetMedication}
                          isDisabled={getResponseLoader || roleAccess([USER_ROLE?.Sp_Standard_User])}
                      
                        />
                      </div>

                      <label className='text-base font-semibold mt-2 block'>
                        Administrated by:
                      </label>
                      <input
                        type='text'
                        readOnly
                        value={administeredByLabel}
                        className='w-full bg-gray-100 text-sm border border-gray-300 font-normal rounded-md px-2 py-2 cursor-default'
                        aria-readonly='true'
                      />

                      <label className='text-base font-semibold mt-2 block'>
                        Administered Time:
                      </label>
                      <input
                        type='text'
                        readOnly
                        value={administeredTimeLabel}
                        className='w-full bg-gray-100 text-sm border border-gray-300 font-normal rounded-md px-2 py-2 cursor-default'
                        aria-readonly='true'
                      />

                      <label className='text-base font-semibold mt-2 block'>
                        Attachments:
                      </label>
                      <div className=''>
                        <MultiFileInput
                          filenameLimit={150}
                          type='any'
                          handleFile={memoizedSetFiles}
                          size={5}
                          maxFilesAllow={3}
                          files={files}
                          handleDelete={(e) => setFiles([...e])}
                          showDragAndDrop={false}
                          handleDeleteItem={handleDeleteItemCall}
                          // isHardClearAllStates={isSubmitting === true}
                          setFilesData={setFilesData}
                          setIsSubmitting={setIsSubmitting}
                          isFileUploadToS3={true}
                          isDisabled={
                            getResponseLoader ||
                            isSubmitting ||
                            isFileDeleteLoader || roleAccess([USER_ROLE?.Sp_Standard_User])
                          }
                          ForMedication={true}
                          deleteFileLoader={isFileDeleteLoader  }
                        />
                      </div>

                      <label className='text-base font-semibold mt-2 block'>
                        Notes:
                      </label>
                      <textarea
                        rows={5}
                        type='text'
                        placeholder='Any notes..'
                        className=' w-full  bg-gray-100 text-sm  border border-gray-300 font-normal  rounded-md'
                        setValue={setValue}
                        {...register("notes", { required: false })}
                        value={watchFormData?.notes}
                        name='notes'
                        defaultValue={""}
                        disabled={getResponseLoader || roleAccess([USER_ROLE?.Sp_Standard_User])}
                      />
                     <label className='text-base font-semibold mt-2 block'>
                        Comments:
                      </label>
                      <textarea
                        rows={3}
                        type='text'
                        placeholder='Enter your comments.'
                        className='w-full bg-gray-100 text-sm border  border-gray-300 font-normal  rounded-md'
                        setValue={setValue}
                        {...register("comments", { required: false })}
                        value={watchFormData?.comments}
                        name='comments'
                        defaultValue={""}
                        disabled={getResponseLoader || roleAccess([USER_ROLE?.Sp_Standard_User])}
                      />
                      </div>
                      <hr className='text-[#C5C5C5] font-bold mt-2' />
                      {
                        !roleAccess([USER_ROLE?.Sp_Standard_User]) && 
                        <div className='w-full flex justify-end mt-4'>
                        <CustomButton
                          label='Save'
                          type='submit'
                          variant=''
                          isDisabled={getResponseLoader || isSubmitting}
                          showLoading={getResponseLoader || isSubmitting}
                          cssClass='bg-[#0DBBB3] text-white px-6'
                        />
                      </div>
                      }
                  
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EditModal;
